import { useQuery } from '@apollo/client';
import debounce from 'lodash.debounce';
import {
  PermissionEnum,
  SessionStorageKeys,
  usePanel,
  UserDto,
} from '@ecdlink/core';
import { setSessionItem } from '../../../../utils/session-storage.utils';
import { UserList } from '@ecdlink/graphql';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ContentLoader } from '../../../../components/content-loader/content-loader';
import { useUser } from '../../../../hooks/useUser';
import UserPanelCreate from '../../components/user-panel-create/user-panel-create';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  SearchIcon,
} from '@heroicons/react/solid';
import {
  Dropdown,
  SearchDropDown,
  SearchDropDownOption,
  Typography,
} from '@ecdlink/ui';
import { useHistory, useLocation } from 'react-router';
import ReactDatePicker from 'react-datepicker';
import { format } from 'date-fns';
import { AdminTypes, Status } from './applications-admins.types';
import UiTable from './components/ui-table';
import { filterByValue } from '../../../../utils/string-utils/string-utils';
import * as styles from '../../../pages.styles';

export const sortByClientStatusOptions: SearchDropDownOption<string>[] = [
  Status?.ACTIVE,
  Status?.INACTIVE,
].map((item) => ({
  id: item,
  label: item,
  value: item,
}));

export default function ApplicationAdmins() {
  const history = useHistory();
  const location = useLocation();

  const { hasPermission, user } = useUser();
  const isSuperAdmin = user?.roles?.some(
    (role: any) => role.systemName === AdminTypes.SuperAdmin
  );

  const [searchValue, setSearchValue] = useState('');
  const [tableData, setTableData] = useState<any[]>([]);

  const panel = usePanel();
  const [statusFilter, setStatusFilter] = useState<
    SearchDropDownOption<string>[]
  >([sortByClientStatusOptions[0]]);
  const [showFilter, setShowFilter] = useState(false);

  // TODO: add pagination
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const [selectedPageSize, setSelectedPageSize] = useState<number>(100);
  const [types, setTypes] = useState<SearchDropDownOption<string>[]>([]);

  const [filterDateAdded, setFilterDateAdded] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const onChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const dateDropdownValue = useMemo(
    () =>
      startDate && endDate
        ? `${format(startDate, 'd MMM yy')} - ${format(endDate, 'd MMM yy')}`
        : '',
    [endDate, startDate]
  );

  const handleSetDateFilter = useCallback(() => {
    setFilterDateAdded(!filterDateAdded);
  }, [filterDateAdded]);

  useEffect(() => {
    if (endDate) {
      handleSetDateFilter();
    }
  }, [endDate]);

  const clearFilters = () => {
    setTypes([]);
    setEndDate(null);
    setStartDate(null);
    setStatusFilter([]);
  };

  const queryVariables = useMemo(() => {
    const filterBy: { fieldName: string; filterType: string; value: string }[] =
      [{ fieldName: 'ADMINISTRATOR', filterType: 'EQUALS', value: 'true' }];

    if (statusFilter?.length === 1) {
      filterBy.push({
        fieldName: 'isActive',
        filterType: 'EQUALS',
        value: statusFilter[0].value === Status.ACTIVE ? 'true' : 'false',
      });
    }

    return {
      search: '',
      order: [{ insertedDate: 'DESC' }, { fullName: 'DESC' }],
      pagingInput: {
        pageNumber: selectedPage,
        pageSize: selectedPageSize,
        filterBy,
      },
    };
  }, [selectedPage, selectedPageSize, statusFilter]);

  const { data, refetch, loading } = useQuery(UserList, {
    variables: queryVariables,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    history.replace({
      pathname: location.pathname,
      state: { queryVariables },
    });
  }, [history, location.pathname, queryVariables]);

  useEffect(() => {
    if (!data?.users) return;

    const modifiedData = data.users
      .map((obj: { [x: string]: any; __typename: any; roles: any }) => {
        const newUserData = {
          ...obj,
          displayColumnIdPassportEmail:
            obj?.email || obj?.userName || obj?.idNumber || '',
        };
        const { __typename: _, roles, ...rest } = newUserData;
        const modifiedRoles = roles.map(
          (role: { [x: string]: any; __typename: any }) => {
            const { __typename: __, ...roleRest } = role;
            return roleRest;
          }
        );
        return { ...rest, roles: modifiedRoles };
      })
      .slice()
      .sort((a, b) =>
        a.insertedDate > b.insertedDate
          ? -1
          : a.insertedDate < b.insertedDate
          ? 1
          : 0
      )
      .map(mapUserTableItem);

    if (startDate && endDate) {
      setTableData(
        modifiedData.filter(
          (d) =>
            new Date(d?.insertedDate).getTime() >=
              new Date(startDate).getTime() &&
            new Date(d?.insertedDate).getTime() <= new Date(endDate).getTime()
        )
      );
    } else {
      setTableData(modifiedData);
    }
  }, [data?.users, endDate, startDate]);

  const displayUserPanel = () => {
    panel({
      noPadding: true,
      title: '',
      render: (onSubmit: any) => (
        <UserPanelCreate
          key={`inviteAdminUser`}
          closeDialog={(userCreated: boolean) => {
            onSubmit();
            if (userCreated) {
              refetch();
            }
          }}
        />
      ),
    });
  };

  const viewSelectedRow = (selectedRow: any) => {
    const role = selectedRow?.roles;

    setSessionItem(
      selectedRow?.userId ?? selectedRow?.id,
      SessionStorageKeys.selectedUser
    );
    history.push({
      pathname: '/users/view-user',
      state: {
        component: role?.[0]?.name,
        userId: selectedRow?.id,
      },
    });
  };

  const mapUserTableItem = (user: UserDto) => {
    return {
      ...user,
      fullName: `${user.firstName} ${user.surname}`,
    };
  };

  const search = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value || '');
  }, 150);

  const hasDateFilter = useMemo(() => (!startDate ? 0 : 1), [startDate]);
  const numberOfFilters = useMemo(
    () => statusFilter?.length + types?.length + hasDateFilter,
    [statusFilter?.length, types?.length, hasDateFilter]
  );

  const renderFilterButtonText = useMemo(() => {
    if (numberOfFilters) {
      if (numberOfFilters === 1) {
        return `${numberOfFilters} Filter`;
      }
      return `${numberOfFilters} Filters`;
    }

    return 'Filter';
  }, [numberOfFilters]);

  if (tableData) {
    return (
      <div>
        <div className="flex flex-col bg-white p-6">
          <div className="pb-5 sm:flex sm:items-center sm:justify-between">
            <div className="text-body w-11/12 sm:flex  sm:justify-around">
              <div className="text-body w-11/12 flex-col sm:flex sm:justify-around">
                <div className="relative w-full">
                  <span className="absolute inset-y-1/2 left-3 mr-4 flex -translate-y-1/2 transform items-center">
                    {searchValue === '' && (
                      <SearchIcon className="h-5 w-5 text-black"></SearchIcon>
                    )}
                  </span>
                  <input
                    className="sm:text-md bg-adminPortalBg block w-full rounded-md py-3 pl-10 pr-3 leading-5 text-gray-900 placeholder-gray-600 focus:border-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-white"
                    placeholder="      Search by email or name..."
                    onChange={search}
                  />
                </div>
                {showFilter && (
                  <div className="items-left mt-4 flex flex-row sm:mt-6">
                    {!filterDateAdded && (
                      <div
                        className="min-w mr-2 flex items-center gap-2"
                        onClick={() => setFilterDateAdded(!filterDateAdded)}
                      >
                        <Dropdown
                          fillType="filled"
                          textColor={'textMid'}
                          fillColor={endDate ? 'secondary' : 'adminPortalBg'}
                          placeholder={dateDropdownValue || 'Date invited'}
                          labelColor={endDate ? 'white' : 'textLight'}
                          list={[]}
                          onChange={(item) => {}}
                          className="w-56 text-sm text-white"
                        />
                      </div>
                    )}
                    {filterDateAdded && (
                      <ReactDatePicker
                        selected={startDate}
                        onChange={onChange}
                        startDate={startDate}
                        endDate={endDate}
                        selectsRange={true}
                        inline
                        shouldCloseOnSelect={true}
                        className="mt-1 h-11 w-64 border-2 border-transparent"
                      />
                    )}

                    <div className="items-left mr-2 flex gap-2">
                      <SearchDropDown<string>
                        displayMenuOverlay={true}
                        menuItemClassName="w-11/12 left-4"
                        className="mb-2 h-11 border-2 border-transparent"
                        color="quatenary"
                        bgColor="white"
                        overlayTopOffset={'120'}
                        options={sortByClientStatusOptions}
                        selectedOptions={statusFilter}
                        onChange={setStatusFilter}
                        placeholder={'Status'}
                        multiple={true}
                        // color={'adminPortalBg'}
                        info={{
                          name: `Status:`,
                        }}
                      />
                    </div>

                    <div className="justify-self z-20 col-end-3 ml-2">
                      <button
                        onClick={clearFilters}
                        type="button"
                        className={styles.clearButton}
                      >
                        Clear All
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="mx-4 w-3/12">
                <span className="w-full text-lg font-medium leading-6 text-gray-900">
                  <button
                    onClick={() => setShowFilter(!showFilter)}
                    id="dropdownHoverButton"
                    className={styles.filterButton}
                    type="button"
                  >
                    <div className="flex items-center gap-1">
                      <Typography
                        className="truncate"
                        type="help"
                        color={numberOfFilters ? 'white' : 'textLight'}
                        text={renderFilterButtonText}
                      />
                      {!showFilter ? (
                        <span>
                          <ChevronDownIcon
                            className={`h-6 w-6 ${
                              numberOfFilters ? 'text-white' : 'text-textLight'
                            }`}
                          />
                        </span>
                      ) : (
                        <span>
                          <ChevronUpIcon
                            className={`h-6 w-6 ${
                              numberOfFilters ? 'text-white' : 'text-textLight'
                            }`}
                          />
                        </span>
                      )}
                    </div>
                  </button>
                </span>
              </div>
            </div>

            <div className="mt-3 justify-end sm:ml-4 sm:mt-0">
              {hasPermission(PermissionEnum.create_user) && isSuperAdmin && (
                <button
                  onClick={displayUserPanel}
                  type="button"
                  className={styles.mainButton}
                >
                  <PlusIcon className="mr-4 h-5 w-5"> </PlusIcon>
                  Create Administrator
                </button>
              )}
            </div>
          </div>

          <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div>
                <UiTable
                  columns={[
                    {
                      field: 'displayColumnIdPassportEmail',
                      use: 'Email/Username/Id',
                    },
                    { field: 'fullName', use: 'name' },
                    { field: 'insertedDate', use: 'Date Invited' },
                    { field: 'isActive', use: 'Active' },
                  ]}
                  viewRow={viewSelectedRow}
                  rows={
                    searchValue !== 'Search by title or content...'
                      ? filterByValue(tableData, searchValue)
                      : tableData
                  }
                  sendRow={true}
                  options={{
                    per_page: selectedPageSize,
                    rows: tableData?.length,
                  }}
                  component={'administrators'}
                  isLoading={loading}
                  refetchData={refetch}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  } else {
    return <ContentLoader />;
  }
}
